"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, UserCheck, Users, CircleDot } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const comparisonDimensions = [{
  dimension: "Team structure",
  consultant: "Single senior specialist directly conducting audits, strategy, technical fixes, and content direction.",
  agency: "Multi-disciplinary team with dedicated specialists across technical SEO, content, outreach, and analytics."
}, {
  dimension: "Communication",
  consultant: "Direct, regular communication with the practitioner actually executing the SEO campaign.",
  agency: "Structured client communication coordinated through dedicated account managers and scheduled status meetings."
}, {
  dimension: "Strategy ownership",
  consultant: "Strategy personally developed and owned end-to-end around your commercial objectives and site constraints.",
  agency: "Strategy developed using established agency frameworks, playbooks, and cross-departmental reviews."
}, {
  dimension: "Flexibility & contracts",
  consultant: "Agile arrangements including modular project audits, focused advisory sprints, and rolling monthly scopes.",
  agency: "Structured annual or fixed multi-month retainers designed to reserve consistent cross-department capacity."
}, {
  dimension: "Decision speed",
  consultant: "Immediate adjustments and fast technical implementation with zero intermediate management layers.",
  agency: "Phased rollout following formal internal review tiers, quality assurance checks, and departmental workflows."
}, {
  dimension: "Best-suited scenario",
  consultant: "SMEs, growing ecommerce stores, and B2B firms looking for focused organic search expertise and dedicated attention.",
  agency: "Enterprise organisations requiring multi-channel digital marketing, heavy production volume, and extensive team scale."
}];
export default function Comparison() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-header", {
        y: 24
      });
      gsap.to(".reveal-header", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".reveal-table", {
        y: 30
      });
      gsap.to(".reveal-table", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".reveal-table",
          start: "top 88%",
          once: true
        }
      });
      gsap.set(".reveal-footer", {
        y: 20
      });
      gsap.to(".reveal-footer", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".reveal-footer",
          start: "top 92%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".reveal-header", ".reveal-table", ".reveal-footer"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section id="consultantvsagencycomparison" ref={container} data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <div className="reveal-header text-center max-w-3xl mx-auto mb-14 md:mb-20" style={{
        opacity: 0
      }}>
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary bg-secondary border-none">
            Clear comparison
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-5">
            Independent consultant vs SEO agency
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Compare how an independent SEO consultant and a digital marketing agency deliver organic search support.
            Both models offer distinct operational strengths depending on your internal team structure, scope, and commercial priorities.
          </p>
        </div>

        {/* Comparison Table Container */}
        <div className="reveal-table rounded-xl border border-border bg-card shadow-sm overflow-hidden mb-12" style={{
        opacity: 0
      }}>
          {/* Desktop & Tablet Table View */}
          <div className="hidden md:block">
            <Table>
              <TableHeader className="bg-muted/60 border-b border-border">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[28%] py-5 px-6 font-semibold text-foreground text-sm uppercase tracking-wider">
                    Dimension
                  </TableHead>
                  <TableHead className="w-[36%] py-5 px-6 font-semibold text-primary text-sm uppercase tracking-wider bg-primary/5 border-x border-border">
                    <div className="flex items-center gap-2">
                      <UserCheck className="size-4 text-primary shrink-0" />
                      <span>YE Consultancy (Yassine)</span>
                    </div>
                  </TableHead>
                  <TableHead className="w-[36%] py-5 px-6 font-semibold text-muted-foreground text-sm uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <Users className="size-4 text-muted-foreground shrink-0" />
                      <span>Traditional SEO Agency</span>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonDimensions.map((item, index) => <TableRow key={item.dimension} data-index={index} className="hover:bg-muted/30 transition-colors border-b border-border last:border-0">
                    <TableCell className="py-5 px-6 font-medium text-foreground align-top text-sm">
                      {item.dimension}
                    </TableCell>
                    <TableCell className="py-5 px-6 text-foreground bg-primary/[0.02] border-x border-border align-top text-sm leading-relaxed">
                      <div className="flex items-start gap-2.5">
                        <CircleDot className="size-3.5 mt-0.5 text-primary shrink-0" aria-hidden="true" />
                        <span>{item.consultant}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-5 px-6 text-muted-foreground align-top text-sm leading-relaxed">
                      <div className="flex items-start gap-2.5">
                        <CircleDot className="size-3.5 mt-0.5 text-muted-foreground/80 shrink-0" aria-hidden="true" />
                        <span>{item.agency}</span>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card List View */}
          <div className="md:hidden divide-y divide-border">
            {comparisonDimensions.map((item, index) => <div key={item.dimension} data-index={index} className="p-5 space-y-4">
                <div className="font-semibold text-foreground text-base">
                  {item.dimension}
                </div>

                <div className="rounded-lg p-3.5 bg-primary/5 border border-border space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                    <UserCheck className="size-3.5" />
                    <span>YE Consultancy (With me)</span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed pl-5">
                    {item.consultant}
                  </p>
                </div>

                <div className="rounded-lg p-3.5 bg-muted/40 border border-border space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Users className="size-3.5" />
                    <span>Traditional Agency</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-5">
                    {item.agency}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Closing Note & Consultation Callout */}
        <div className="reveal-footer rounded-xl bg-card border border-border p-6 sm:p-8 lg:p-10 shadow-xs" style={{
        opacity: 0
      }}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-2xl space-y-4">
              <h3 className="font-default text-xl sm:text-2xl font-semibold text-foreground">
                Which model fits your business?
              </h3>
              <div className="space-y-3 text-sm sm:text-base leading-relaxed text-muted-foreground">
                <p>
                  <strong className="font-semibold text-foreground">When an independent consultant fits best:</strong> Businesses seeking focused organic search expertise, agile technical execution, and direct practitioner collaboration. If you are preparing to{" "}
                  <Link to="/HireSeoConsultantPage" className="text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors">
                    hire an SEO consultant
                  </Link>
                  , this route gives you dedicated ownership and senior attention without layered agency management.
                </p>
                <p>
                  <strong className="font-semibold text-foreground">When a full agency fits best:</strong> Larger organisations requiring multi-channel marketing campaigns, high-volume content production across disparate platforms, or extensive team redundancy to support enterprise-scale rollouts.
                </p>
              </div>
            </div>
            <div className="shrink-0 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 py-2.5 shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a free consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}